import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { 
  Button, 
  Container, 
  Box, 
  Typography, 
  TextField,
  Divider,
  Alert,
  CircularProgress,
  Paper,
  InputAdornment,
  IconButton,
  ThemeProvider,
  createTheme
} from '@mui/material'
import { Google as GoogleIcon, Visibility, VisibilityOff } from '@mui/icons-material'
import { useAuth } from '../contexts/AuthContext'
import { supabase } from '../utils/supabase'
import '../styles/LoginForm.css'

// Create a custom theme just for the registration form
const registrationTheme = createTheme({
  palette: {
    primary: {
      main: '#8A42EC',
    },
  },
  components: {
    MuiTextField: {
      styleOverrides: {
        root: {
          marginBottom: '20px',
          '& .MuiOutlinedInput-root': {
            '& fieldset': {
              borderColor: '#8A42EC',
            },
            '&:hover fieldset': {
              borderColor: '#6200ee',
            },
            '&.Mui-focused fieldset': {
              borderColor: '#6200ee',
            },
          },
          '& .MuiInputLabel-root': {
            color: '#8A42EC',
          },
          '& .MuiInputBase-input': {
            height: '20px',
            padding: '15px',
          },
        }
      }
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500,
          fontSize: '16px',
        }
      }
    }
  }
});

export const CompleteRegistration = () => {
  const { signInWithGoogle, signUpWithEmail, error: authError, setError } = useAuth()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [validating, setValidating] = useState(true)
  const [formError, setFormError] = useState('')
  const [successMessage, setSuccessMessage] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [invitationData, setInvitationData] = useState(null)
  const [token, setToken] = useState(null)

  // Verify invitation token on component mount
  useEffect(() => {
    const verifyInvitation = async () => {
      const invitationToken = searchParams.get('token')
      
      if (!invitationToken) {
        setFormError('Invalid invitation link. Please contact your administrator.')
        setValidating(false)
        return
      }

      setToken(invitationToken)

      try {
        const apiBaseUrl = process.env.REACT_APP_API_URL || 'https://pulse.marketersindemand.com'
        const response = await fetch(`${apiBaseUrl}/api/invitations/verify-token/${invitationToken}`)
        
        if (response.ok) {
          const data = await response.json()
          setInvitationData(data)
          setEmail(data.email)
          console.log('Valid invitation found:', data)
        } else {
          const error = await response.json()
          setFormError(error.error || 'Invalid or expired invitation')
        }
      } catch (error) {
        console.error('Error verifying invitation:', error)
        setFormError('Error validating invitation. Please try again.')
      } finally {
        setValidating(false)
      }
    }

    verifyInvitation()
  }, [searchParams])

  const handlePasswordRegistration = async (e) => {
    e.preventDefault()
    setLoading(true)
    setFormError('')
    setSuccessMessage('')
    setError(null)

    // Validate passwords match
    if (password !== confirmPassword) {
      setFormError('Passwords do not match')
      setLoading(false)
      return
    }

    // Validate password strength
    if (password.length < 8) {
      setFormError('Password must be at least 8 characters long')
      setLoading(false)
      return
    }

    try {
      console.log('Creating account with password for invited user:', email)
      const result = await signUpWithEmail(email, password, token)
      
      if (result && result.success) {
        setSuccessMessage('Account created successfully! Redirecting...')
        // Redirect to root and let RoleBasedRedirect handle the routing
        setTimeout(() => {
          window.location.href = '/';
        }, 1000);
      } else {
        setFormError('Registration completed but there may have been an issue. Please try signing in.')
      }
    } catch (err) {
      console.error('Registration error:', err)
      setFormError(err.message)
    } finally {
      setLoading(false)
    }
  }

  const handleGoogleSignIn = async () => {
    try {
      setLoading(true)
      setFormError('')
      setError(null)
      console.log('Signing in with Google for invited user:', email)
      await signInWithGoogle()
      // Redirect to root and let RoleBasedRedirect handle the routing
      setTimeout(() => {
        window.location.href = '/';
      }, 1000);
    } catch (err) {
      console.error('Google sign in error:', err)
      setFormError('Failed to sign in with Google')
    } finally {
      setLoading(false)
    }
  }

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword)
  }

  const handleClickShowConfirmPassword = () => {
    setShowConfirmPassword(!showConfirmPassword)
  }

  if (validating) {
    return (
      <ThemeProvider theme={registrationTheme}>
        <Container component="main" maxWidth="xs">
          <Box
            sx={{
              marginTop: 8,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <Paper elevation={3} sx={{ p: 4, width: '100%', textAlign: 'center' }}>
              <CircularProgress sx={{ color: '#8A42EC', mb: 2 }} />
              <Typography>Validating your invitation...</Typography>
            </Paper>
          </Box>
        </Container>
      </ThemeProvider>
    )
  }

  if (!invitationData) {
    return (
      <ThemeProvider theme={registrationTheme}>
        <Container component="main" maxWidth="xs">
          <Box
            sx={{
              marginTop: 8,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <Paper elevation={3} sx={{ p: 4, width: '100%' }}>
              <Typography component="h1" variant="h4" sx={{ mb: 4, color: '#8A42EC', textAlign: 'center' }}>
                Invalid Invitation
              </Typography>
              <Alert severity="error" sx={{ mb: 2 }}>
                {formError}
              </Alert>
              <Button
                fullWidth
                variant="outlined"
                onClick={() => navigate('/')}
                sx={{ color: '#8A42EC', borderColor: '#8A42EC' }}
              >
                Return to Login
              </Button>
            </Paper>
          </Box>
        </Container>
      </ThemeProvider>
    )
  }

  return (
    <ThemeProvider theme={registrationTheme}>
      <Container component="main" maxWidth="xs">
        <Box
          sx={{
            marginTop: 8,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Paper elevation={3} sx={{ p: 4, width: '100%' }}>
            <Typography component="h1" variant="h4" sx={{ mb: 2, color: '#8A42EC', textAlign: 'center' }}>
              Complete Registration
            </Typography>
            
            <Typography variant="body1" sx={{ mb: 3, textAlign: 'center', color: '#666' }}>
              Welcome! You've been invited to join Pulse as a <strong>{invitationData.role}</strong>.
            </Typography>

            <Typography variant="body2" sx={{ mb: 3, textAlign: 'center', color: '#888' }}>
              Account: <strong>{email}</strong>
            </Typography>

            {(formError || authError) && (
              <Alert 
                severity="error"
                sx={{ mb: 2, width: '100%' }}
              >
                {formError || authError}
              </Alert>
            )}

            {successMessage && (
              <Alert 
                severity="success"
                sx={{ mb: 2, width: '100%' }}
              >
                {successMessage}
              </Alert>
            )}

            <Button
              fullWidth
              variant="contained"
              color="primary"
              startIcon={<GoogleIcon />}
              onClick={handleGoogleSignIn}
              disabled={loading}
              sx={{ mb: 2, height: '50px' }}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : 'Continue with Google'}
            </Button>

            <Divider sx={{ width: '100%', mb: 2 }}>OR</Divider>

            <Typography variant="body2" sx={{ mb: 2, textAlign: 'center', color: '#666' }}>
              Set up a password for your account:
            </Typography>

            <form onSubmit={handlePasswordRegistration} className="login-form basic-form">
              <div className="form-group">
                <label htmlFor="password">Password <span className="required">*</span></label>
                <div className="password-field">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    id="password"
                    name="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    autoComplete="new-password"
                    placeholder="Enter a secure password"
                  />
                  <button
                    type="button"
                    className="toggle-password"
                    onClick={handleClickShowPassword}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? '👁‍🗨' : '👁'}
                  </button>
                </div>
              </div>
              
              <div className="form-group">
                <label htmlFor="confirmPassword">Confirm Password <span className="required">*</span></label>
                <div className="password-field">
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    id="confirmPassword"
                    name="confirmPassword"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    autoComplete="new-password"
                    placeholder="Confirm your password"
                  />
                  <button
                    type="button"
                    className="toggle-password"
                    onClick={handleClickShowConfirmPassword}
                    aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                  >
                    {showConfirmPassword ? '👁‍🗨' : '👁'}
                  </button>
                </div>
              </div>
              
              <button
                type="submit"
                className="submit-button"
                disabled={loading}
              >
                {loading ? "Creating Account..." : "Complete Registration"}
              </button>
            </form>
          </Paper>
        </Box>
      </Container>
    </ThemeProvider>
  )
} 